import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, Ship, Building2, Home as HomeIcon, PartyPopper, UsersRound, Briefcase, Heart, Compass, ArrowRight, ArrowDown, Check } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { TH, THAILAND_BOOKING_URL, PRICING } from "@/lib/thailand";
import { track } from "@/lib/analytics";
import { Reveal, MaskLine } from "@/components/Reveal";
import { SectionHeading, FeatureGrid, CTABand } from "@/components/PageBlocks";
import PassportListForm from "@/components/PassportListForm";
import Testimonials from "@/components/Testimonials";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const OFFERINGS = [
  { icon: Ship, title: "Cruises", desc: "We help you compare ships, itineraries, and cabins to find the right sailing.", to: "/cruises" },
  { icon: Building2, title: "All-Inclusive Resorts", desc: "We match you to a resort that fits your style, budget, and who's traveling.", to: "/resorts" },
  { icon: HomeIcon, title: "Villas & Private Escapes", desc: "We find private homes and villas with the space and privacy your group needs.", to: "/resorts" },
  { icon: PartyPopper, title: "Destination Weddings & Celebrations", desc: "We help plan celebrations abroad so you can focus on the moment.", to: "/resorts" },
  { icon: UsersRound, title: "Family & Friends Getaways", desc: "We organize the details so everyone travels together with ease.", to: "/group-travel" },
  { icon: Briefcase, title: "Corporate & Group Retreats", desc: "We plan retreats and incentive trips that run smoothly start to finish.", to: "/group-travel" },
  { icon: Heart, title: "Honeymoons & Romantic Getaways", desc: "We plan a honeymoon built around the two of you.", to: "/honeymoons" },
  { icon: Compass, title: "Custom Travel Experiences", desc: "Tell us what you're imagining and we'll help build the trip around it.", to: "/experiences" },
];

const DESTINATIONS = [
  { img: IMAGES.dest1, name: "Beaches & Islands", tag: "Sun & Sea" },
  { img: IMAGES.dest2, name: "Europe & Mediterranean", tag: "Cities & Coast" },
  { img: IMAGES.expedition, name: "Adventure & Nature", tag: "Explore" },
];

const TRUST = [
  { title: "20+ Years", sub: "of combined travel expertise" },
  { title: "Group Travel", sub: "is our specialty" },
  { title: "Personal Service", sub: "a dedicated specialist on every trip" },
  { title: "Hosted Series", sub: "The Passport Experience™" },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[94vh] min-h-[560px] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Ocean-view resort at dusk" fetchPriority="high" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/45 to-black/55" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(120% 90% at 12% 92%, rgba(5,5,5,0.85) 0%, rgba(5,5,5,0.35) 40%, transparent 70%)" }} />

      <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-28">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
          className="font-body text-[11px] md:text-xs uppercase tracking-[0.45em] text-gold mb-6 [text-shadow:0_2px_10px_rgba(0,0,0,0.75)]">
          The Passport Experience
        </motion.p>
        <h1 className="font-serif font-light tracking-tighter leading-[0.9] text-[2.4rem] sm:text-6xl lg:text-8xl text-ink max-w-5xl [text-shadow:0_2px_20px_rgba(0,0,0,0.6)]">
          <MaskLine delay={0.35}>Luxury Group Travel,</MaskLine>
          <MaskLine delay={0.5}><span className="italic text-gold">Thoughtfully Designed.</span></MaskLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }}
          className="font-body text-white/80 mt-8 max-w-2xl text-base md:text-lg leading-relaxed font-light">
          From milestone celebrations and group getaways to cruises, resorts, and honeymoons, Inforno Travel handles the
          details so you can enjoy the journey.
        </motion.p>
        {/* Two-path journey: planning help (primary) vs. ready-to-book (secondary) */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }}
          className="mt-10 flex flex-col sm:flex-row gap-5 sm:gap-8">
          <div>
            <Link to="/start-your-journey" data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center border border-gold bg-gold text-black px-9 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-transparent hover:text-gold transition-colors duration-300">
              Start Your Journey
            </Link>
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/50 mt-2.5">For travelers who want help planning</p>
          </div>
          <div>
            <Link to="/ReadyToBook" data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center border border-white/40 text-ink px-9 py-4 font-body text-xs uppercase tracking-[0.22em] hover:border-gold hover:text-gold transition-colors duration-300">
              Ready to Book
            </Link>
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/50 mt-2.5">Already know what you want</p>
          </div>
        </motion.div>
        <motion.a href="#trust" data-testid="hero-continue" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
          onClick={(e) => { e.preventDefault(); document.getElementById("trust")?.scrollIntoView({ behavior: "smooth" }); }}
          className="hidden sm:flex absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-1.5 text-white/60 hover:text-gold transition-colors">
          <span className="font-body text-[10px] uppercase tracking-[0.3em]">Explore Inforno</span>
          <ArrowDown size={14} className="text-gold" />
        </motion.a>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["Group Travel Made Easy", "We Handle the Details", "Enjoy the Journey", "The Passport Experience"];
  const row = [...items, ...items, ...items, ...items];
  return (
    <div data-testid="marquee" className="py-10 border-y border-white/10 overflow-hidden bg-noir">
      <div className="marquee-track">
        {row.map((t, i) => (
          <span key={i} className="font-serif italic text-3xl md:text-5xl font-light text-white/40 mx-10">
            {t} <span className="text-gold not-italic mx-6">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function TrustStrip() {
  return (
    <section id="trust" data-testid="trust-strip" className="py-14 md:py-16 px-6 md:px-12 bg-noir">
      <div className="max-w-[1500px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {TRUST.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.06}>
            <div className="bg-noir px-6 py-8 text-center h-full">
              <p className="font-serif text-2xl md:text-3xl font-light text-gold">{t.title}</p>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] text-white/50 mt-2">{t.sub}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function GroupSpotlight() {
  const points = ["Milestone & family celebrations", "Reunions & friends' getaways", "Corporate retreats & incentive trips", "Group cruises, resorts & villas"];
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-noir">
      <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <div className="relative overflow-hidden border border-white/10 aspect-[4/3]">
              <img src={IMAGES.cruise} alt="Group cruise at sea" loading="lazy" className="w-full h-full object-cover brightness-[0.85]" />
              <span className="absolute top-6 left-6 font-body text-[10px] uppercase tracking-[0.3em] text-gold bg-black/60 px-4 py-2 backdrop-blur-sm">Our Specialty</span>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal delay={0.1}>
            <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-5">Group Travel</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light leading-[0.98] tracking-tight">Traveling as a group?<br /><span className="italic text-white/60">We make it easy.</span></h2>
            <p className="font-body text-white/60 mt-6 text-sm md:text-base leading-relaxed font-light">
              Great group trips bring people together — but the planning can pull you in a hundred directions. We help
              organize the pieces so you spend less time coordinating and more time looking forward to the trip.
            </p>
            <ul className="mt-7 space-y-3.5">
              {points.map((p) => (
                <li key={p} className="flex items-center gap-4 text-white/75 font-body text-sm">
                  <span className="w-7 h-7 border border-gold/40 flex items-center justify-center text-gold shrink-0"><Check size={13} /></span>{p}
                </li>
              ))}
            </ul>
            <Link to="/group-travel" className="mt-9 inline-flex items-center gap-3 border border-gold text-gold px-8 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-gold hover:text-black transition-colors duration-300">
              Explore Group Travel <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Offerings() {
  return (
    <section id="services" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-y border-white/10">
      <div className="max-w-[1500px] mx-auto">
        <div className="mb-16">
          <SectionHeading overline="What We Do" title={<>How Inforno helps you travel</>} sub="Every trip is planned by a Travel Experience Specialist and built around the way you like to travel." />
        </div>
        <FeatureGrid items={OFFERINGS} />
        <div className="mt-10 text-center">
          <Link to="/experiences" className="inline-flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.2em] link-underline">See all experiences <ArrowRight size={13} /></Link>
        </div>
      </div>
    </section>
  );
}

function DestinationsPreview() {
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-noir">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <SectionHeading overline="Destinations" title={<>Not sure where yet?<br /><span className="italic text-white/60">That's the fun part.</span></>} />
          <Reveal delay={0.1}><Link to="/destinations" className="inline-flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.2em] link-underline">Explore destinations <ArrowRight size={13} /></Link></Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {DESTINATIONS.map((d, i) => (
            <Reveal key={d.name} delay={i * 0.1}>
              <Link to="/destinations" data-testid={`destination-${i}`} className="group relative block overflow-hidden border border-white/10 aspect-[4/5]">
                <img src={d.img} alt={d.name} loading="lazy" className="w-full h-full object-cover brightness-[0.7] group-hover:brightness-100 group-hover:scale-[1.05] transition-all duration-[1200ms] ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-8">
                  <p className="font-body text-[10px] uppercase tracking-[0.3em] text-gold mb-2">{d.tag}</p>
                  <h3 className="font-serif text-3xl font-light">{d.name}</h3>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ThailandFeature() {
  const facts = [
    "Bangkok • Phuket  |  June 19–26, 2027",
    "7 Nights • 5-Star Stays • Curated Experiences",
    `Starting at ${PRICING.fromPrice} per person, double occupancy`,
    `Reserve with ${PRICING.deposit} per person`,
  ];
  return (
    <section data-testid="thailand-feature" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-y border-white/10">
      <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-5">Featured · Inforno Travel Presents</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light leading-[0.98] tracking-tight">The Passport Experience™:<br /><span className="italic text-gold">Thailand 2027</span></h2>
            <p className="font-body text-white/60 mt-6 text-sm md:text-base leading-relaxed font-light">Your passport gets you there. The Passport Experience takes you beyond.</p>
            <ul className="mt-7 space-y-3">
              {facts.map((f) => (
                <li key={f} className="flex items-center gap-3 font-body text-sm text-white/75">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full shrink-0" />{f}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/Thailand2027" data-testid="thailand-feature-explore"
                className="inline-flex items-center justify-center border border-gold bg-gold text-black px-8 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-transparent hover:text-gold transition-colors duration-300">
                Explore Thailand 2027
              </Link>
              <a href={THAILAND_BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="thailand-feature-reserve"
                onClick={() => track("reserve_your_spot_click", { source_page: "Home", cta_location: "thailand_feature" })}
                className="inline-flex items-center justify-center border border-white/40 text-ink px-8 py-4 font-body text-xs uppercase tracking-[0.22em] hover:border-gold hover:text-gold transition-colors duration-300">
                Reserve Your Spot
              </a>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            <Link to="/Thailand2027" className="group relative block overflow-hidden border border-white/10 aspect-[4/3]">
              <img src={TH.riverCruise} alt="Bangkok's Chao Phraya River illuminated at night" loading="lazy"
                className="w-full h-full object-cover brightness-[0.75] group-hover:brightness-95 group-hover:scale-[1.04] transition-all duration-[1200ms]" />
              <span className="absolute top-6 left-6 font-body text-[10px] uppercase tracking-[0.3em] text-gold bg-black/60 px-4 py-2 backdrop-blur-sm">June 19–26, 2027</span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function PassportBand() {
  return (
    <section data-testid="passport-band" className="py-16 md:py-20 px-6 md:px-12 bg-noir">
      <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 lg:items-end">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-4">The Passport List</p>
            <h2 className="font-serif text-3xl md:text-4xl font-light leading-[1.02]">New trips, first. <span className="italic text-white/60">No noise.</span></h2>
            <p className="font-body text-white/55 text-sm mt-4 font-light max-w-md">Trip ideas, travel news and special announcements from Inforno Travel. Unsubscribe anytime.</p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}><PassportListForm sourcePage="Home" compact /></Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo({ title: "Inforno Travel — Luxury Group Travel, Thoughtfully Designed", description: "Inforno Travel plans luxury group trips, cruises, resorts, honeymoons, and celebrations. We handle the details so you can enjoy the journey." });
  return (
    <div id="top" className="bg-noir">
      <Navbar />
      <Hero />
      <Marquee />
      <TrustStrip />
      <ThailandFeature />
      <GroupSpotlight />
      <Offerings />
      <Testimonials />
      <DestinationsPreview />
      <PassportBand />
      <CTABand eyebrow="Ready when you are" title="Where will your passport take you next?" showRtb />
      <Footer />
    </div>
  );
}
